import React, { useState, useEffect, useRef, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useIsMobile, setPagination } from 'utils'
import { Button, Dropdown } from 'antd'
import classNames from 'classnames'
import {
  confirmDelete,
  getCopyFunc,
  errorTranslationMessage,
  ArrowLeftIcon,
  confirmBase,
} from 'utils/ui'
import { SERVICE_NAME } from 'service'
import FeatherIcon from 'components/FeatherIcon'
import DomainIcon from 'components/DomainIcon'
import CardView from 'pages/records/components/CardView'
import ResourceService from 'api/Resource'
import { RSAEncDec } from 'pUtils'

import RecordCardContent from 'pages/records/components/RecordCardContent'
import RecordLabelModal from 'pages/records/components/RecordLabel'
import { SRC_TYPE, IMPORT_EXPORT_CONFIG } from 'pages/records/components/helper'
import { getRecordServiceKey } from 'pages/records/components/MemoEditorModal/memoRecord'
import useRecordExport from 'pages/records/components/RecordExport'
import { FILE_ACCESS_LEVEL } from 'types/utils'
import styles from 'pages/records/components/CardView/style.module.scss'
import FavoritesService from 'api/Favorite'
import { featureFlags } from 'config/feature-flags'
import closeIcon from '@/assets/images/icon-close-24.svg'
import editIcon from '@/assets/images/icon-edit.svg'
import historyIcon from '@/assets/images/icon-history-24.svg'
import shareIcon from '@/assets/images/icon-share.svg'
import favoriteIcon from '@/assets/images/icon-favorite.svg'
import favoriteFillIcon from '@/assets/images/icon-favorite-fill.svg'
import dotVerticalIcon from '@/assets/images/icon-dot-vertical.svg'
import defaultIcon from '@/assets/images/defaultRecordIcon.png'
import labelIcon from '@/assets/images/icon-tag-24.svg'
import exportIcon from '@/assets/images/icon-export-24.svg'

import styles0 from './style.module.scss'

export const setIcon = (individual, callback) => {
  let domainImage = null

  const expiredUrlItem = individual && (individual.url || individual.template_icon)

  const urlItem = individual.items && individual.items.find((value) => value.type === 'url')
  let curUrl = urlItem && urlItem.content ? urlItem.content : expiredUrlItem

  let domain = null
  if (curUrl && curUrl.indexOf('http://') !== 0 && curUrl.indexOf('https://') !== 0) {
    curUrl = `https://${curUrl}`
  }

  if (curUrl) {
    try {
      domain = new URL(curUrl).hostname
      if (domain.startsWith('www.')) {
        domain = domain.substring(4)
      }
    } catch (e) {
      console.log(' --- domain error ', e)
      domain = null
    }
  }

  const attrs = {
    classNames: styles.DetailCardHeaderImage,
    domain,
    size: 72,
    alt: individual.title,
  }

  let defaultElem = null
  if (individual.template != null || individual.template_icon != null) {
    // 보안(인증 코드) 템플릿 아이콘 'shield' → 'shield-check' 매핑은 FeatherIcon 이 일괄 처리.
    const templateIcon = individual.template?.icon || individual.template_icon

    if (templateIcon != null) {
      const iconAttrs = {
        name: templateIcon,
        width: '32',
        height: '32',
        stroke: '#3362FF',
      }

      defaultElem = <FeatherIcon {...iconAttrs} />
    }
  }

  if (defaultElem != null) {
    attrs.defaultElem = defaultElem
  } else {
    attrs.defaultUrl = defaultIcon
  }

  domainImage = <DomainIcon {...attrs} />

  callback(domainImage)
}

const DetailContent = (props) => {
  const {
    openItem,
    id,
    messageApi,
    individual,
    reload,
    reloadRecord,
    loading,
    pageInfo,
    orgChartData,
  } = props
  // 사용자 노출 문구의 {{service}} 단어 — 메모 레코드면 "메모", 아니면 브랜딩 서비스명
  const serviceKey = getRecordServiceKey(individual, SERVICE_NAME)
  const messageWrapper = (msgType, trueMsg, falseMsg) => {
    messageApi.open({
      type: msgType,
      content: isFavorite === true ? trueMsg : falseMsg,
    })
  }

  const SuccessMessage = () =>
    messageWrapper(
      'success',
      t('workspace.result.deleteFavoriteRecord', { service: t(serviceKey) }),
      t('workspace.result.addFavoriteRecord', { service: t(serviceKey) }),
    )

  const ErrorMessage = () =>
    messageWrapper(
      'error',
      t('workspace.result.errorFavoriteRecord', { service: t(serviceKey) }),
      t('workspace.result.errorDeleteFavoriteRecord', { service: t(serviceKey) }),
    )

  const deleteSuccessMessage = () => {
    messageApi.open({
      key: 'record_delete_success',
      type: 'success',
      content: t('workspace.record.delete', { service: t(serviceKey) }),
    })
  }

  const [domainImageElem, setDomainImageElem] = useState(null)
  const [copyResultHidden, setCopyResultHidden] = useState(true)
  const [labelOpen, setLabelOpen] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const timerRef = useRef(null)
  const showZeroDarkWeb = useSelector((state) => state.settings.showZeroDarkWeb)
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const isOwner = individual != null && individual.permission === SRC_TYPE.RESOURCE_OWNER
  const record = individual || {}
  // 만료(431) 상태의 즐겨찾기 여부는 expiredData 로 실려 온다 (백엔드 async_check_expired 응답)
  const isFavorite = record.is_favorite ?? record.expiredData?.is_favorite

  const isExpired = record != null && record.expired === true
  const OwnerAndAdmin = record && (isOwner || record.permission === SRC_TYPE.GROUP_ADMIN)
  const isMobile = useIsMobile(1029)

  const closeItem = () => {
    openItem(null)
  }

  const handleFavoriteSubmit = async () => {
    try {
      // 만료 레코드는 individual.id 가 없고 expiredData.id 만 있다 (즐겨찾기는 만료돼도 토글 허용)
      const targetId = individual.id ?? individual.expiredData?.id
      let response
      if (isFavorite) {
        response = await FavoritesService.delete({ id: targetId })
      } else {
        response = await FavoritesService.post({ target_id: targetId })
      }

      if (response.code === 200) {
        dispatch({
          type: 'menu/RELOAD',
        })
        reload()
        reloadRecord(() => {
          SuccessMessage()
        })
      }

      if (response.code === 400) {
        reload(() => ErrorMessage())
      }
    } catch (error) {
      console.log(error)
      ErrorMessage()
    }
  }

  const handleDeleteSubmit = async () => {
    let ret

    try {
      ret = await ResourceService.delete({ id: individual.id })

      if (ret.code === 200) {
        setPagination(pageInfo)
        deleteSuccessMessage()
        // reload(deleteSuccessMessage)
        dispatch({
          type: 'menu/RELOAD',
        })
        // 삭제로 줄어든 사용량을 사이드바 용량 게이지에 반영 (license 재조회)
        dispatch({ type: 'user/RELOAD_PAID_LICENSE' })
        closeItem()
      }
    } catch (error) {
      const { code } = error
      if (error != null) {
        errorTranslationMessage(code, t, messageApi)
      }
      closeItem()
    }
  }

  const showCopyResult = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    setCopyResultHidden(false)

    timerRef.current = setTimeout(() => {
      setCopyResultHidden(true)
      timerRef.current = null
    }, 2000)
  }

  useEffect(() => {
    if (individual != null) {
      if (individual.expired) {
        setIcon(individual.expiredData, (domainImage) => {
          setDomainImageElem(domainImage)
        })
      } else {
        setIcon(individual, (domainImage) => {
          setDomainImageElem(domainImage)
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [individual, showZeroDarkWeb])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const allowFileDownload = individual?.headerAccessLevel === FILE_ACCESS_LEVEL.DOWNLOAD_AND_PREVIEW
  const { handleExport } = useRecordExport(individual, {
    t,
    messageApi,
    setExportLoading,
    allowFileDownload,
  })

  let dropdownItems = []
  if (individual) {
    const baseItems = [
      isOwner && {
        label: t('appConfig.menu.share'),
        key: '1',
        onClick: () => openItem('share', id),
      },
      OwnerAndAdmin && {
        label: t('appConfig.menu.edit'),
        key: '0',
        onClick: () => openItem('edit', id),
      },
      {
        label: isFavorite ? t('appConfig.menu.removeFavorite') : t('appConfig.menu.addFavorite'),
        key: '2',
        onClick: handleFavoriteSubmit,
      },
      {
        label: t('appConfig.menu.labelAs'),
        key: '3',
        onClick: () => {
          setLabelOpen(true)
        },
      },
      {
        label: t('appConfig.menu.shareWithLink'),
        key: '5',
        onClick: () => {
          confirmBase({
            icon: 'navy',
            title: t('appConfig.menu.shareWithLink'),
            content: t('appConfig.confirm.shareLinkInfo'),
            okText: t('common.button.ok'),
            onOk: () => {
              const url = `${window.location.origin}/records/all?targetId=${individual.id}`
              const copyLink = getCopyFunc({ value: url })
              copyLink()
              messageApi.open({
                type: 'success',
                content: t('appConfig.result.recordLinkCopied', { service: t(serviceKey) }),
              })
            },
          })
        },
      },
      IMPORT_EXPORT_CONFIG.enableExport && {
        label: t('appConfig.menu.export'),
        key: '6',
        onClick: handleExport,
        disabled: exportLoading,
      },
    ].filter(Boolean)

    const deleteItems = isOwner
      ? [
          { type: 'divider' },
          {
            label: <span style={{ color: 'red' }}>{t('appConfig.menu.delete')}</span>,
            key: '4',
            onClick: () =>
              confirmDelete({
                icon: 'red',
                title: t('appConfig.title.deleteRecord', { service: t(serviceKey) }),
                content: t('appConfig.confirm.deleteRecord', {
                  record: individual.title,
                  service: t(serviceKey),
                }),
                okText: t('common.button.delete'),
                onOk: handleDeleteSubmit,
              }),
          },
        ]
      : []

    dropdownItems = [...baseItems.filter(Boolean), ...deleteItems]
  }

  const headerDropdownAttrs = {
    trigger: ['click'],
    menu: { items: dropdownItems },
    overlayClassName: classNames(styles.recordHeaderDropdown),
  }

  // header permission에 따라 버튼 다르게 보여주기
  const [manageLabelOpen, setManageLabelOpen] = useState(false)
  let headerBtns = null
  if (individual != null) {
    const { permission } = individual

    headerBtns = (
      <React.Fragment>
        {/* 공유·편집과 달리 권한 게이트 없음 — 레코드를 볼 수 있으면 히스토리도 볼 수 있다
            (서버도 읽기 권한만 요구). 만료 레코드에서는 다른 버튼들과 동일하게 숨김 — 서버 430 과 일관. */}
        {!isExpired && featureFlags.recordChangeHistory && (
          <Button
            type="text"
            onClick={() => openItem('history', id)}
            icon={<img src={historyIcon} alt="history" />}
          />
        )}

        {isExpired ? null : (
          <Button
            type="text"
            onClick={() => {
              setLabelOpen(true)
              setManageLabelOpen(false)
            }}
            icon={<img src={labelIcon} alt="label" />}
          />
        )}

        {!isExpired && IMPORT_EXPORT_CONFIG.enableExport && (
          <Button
            type="text"
            icon={<img src={exportIcon} alt="export" />}
            onClick={handleExport}
            loading={exportLoading}
          />
        )}

        {isOwner && (
          <Button
            type="text"
            icon={<img src={shareIcon} alt="share" />}
            onClick={() => openItem('share', id)}
          />
        )}
        {(isOwner || permission === SRC_TYPE.GROUP_ADMIN) && (
          <Button
            type="text"
            icon={<img src={editIcon} alt="edit" />}
            onClick={() => openItem('edit', id)}
          />
        )}
      </React.Fragment>
    )
  }

  // 헤더 렌더링 처리
  const header = (
    <Fragment>
      <div>
        {isMobile ? (
          <ArrowLeftIcon onClick={closeItem} />
        ) : (
          <React.Fragment>
            {domainImageElem}
            {/* 즐겨찾기는 만료 레코드에서도 토글 가능 — 백엔드가 만료 검사 없이 허용 */}
            <Button
              type="text"
              icon={
                <img src={isFavorite === true ? favoriteFillIcon : favoriteIcon} alt="favorite" />
              }
              className={classNames(styles.favoriteIcon)}
              onClick={handleFavoriteSubmit}
            />
          </React.Fragment>
        )}
      </div>

      <div>
        {headerBtns}

        {isExpired ? null : (
          <RecordLabelModal
            labelOpen={labelOpen}
            setLabelOpen={setLabelOpen}
            manageLabelOpen={manageLabelOpen}
            setManageLabelOpen={setManageLabelOpen}
            reloadRecord={reloadRecord}
            reload={reload}
            individual={individual}
            messageApi={messageApi}
          />
        )}

        {isExpired ? null : (
          <Dropdown {...headerDropdownAttrs}>
            <Button type="text" icon={<img src={dotVerticalIcon} alt="dot-vertical" />} />
          </Dropdown>
        )}

        {isMobile ? null : (
          <Button type="text" icon={<img src={closeIcon} alt="delete" />} onClick={closeItem} />
        )}
      </div>
    </Fragment>
  )

  const handleCopy = getCopyFunc({ value: record.title })

  const handleTransferOwnership = async (newOwnerId) => {
    try {
      const rsaObj = new RSAEncDec({ isLogin: true })

      const result = await rsaObj.transferResourceOwnership(id, newOwnerId)

      if (result) {
        dispatch({
          type: 'menu/RELOAD',
        })
        // 개인 저장용량은 owner_id 귀속이라(유저 플랜 PRO) 소유권을 넘기면 내 사용량이
        // 그만큼 줄어든다 — 넘긴 사람 화면의 게이지를 즉시 맞춘다.
        dispatch({ type: 'user/RELOAD_PAID_LICENSE' })
        reload()
        reloadRecord(() => {
          messageApi.open({
            type: 'success',
            content: t('workspace.result.transferOwnership', { service: t(serviceKey) }),
          })
        })
      }
    } catch (error) {
      console.log('Transfer ownership error:', error)
      const { message: code } = error
      errorTranslationMessage(code, t, messageApi)

      // if (code != null) {
      //   errorTranslationMessage(code, t, messageApi)
      // } else {
      //   messageApi.open({
      //     type: 'error',
      //     content: t('workspace.error.transferOwnership', { service: t(serviceKey) }),
      //   })
      // }
    }
  }

  const handleMenuClick = (key, menuRecord) => {
    if (key === 'transfer') {
      confirmBase({
        icon: 'navy',
        title: t('workspace.confirm.transferOwnershipTitle'),
        content: t('workspace.confirm.transferOwnershipContent', { name: menuRecord.name }),
        okText: t('common.button.confirm'),
        onOk: () => handleTransferOwnership(menuRecord.id),
      })
    }
  }

  return (
    <CardView header={header}>
      {loading ? null : (
        <RecordCardContent
          individual={individual}
          domainImageElem={domainImageElem}
          labelOpen={labelOpen}
          setLabelOpen={setLabelOpen}
          handleCopy={handleCopy}
          showCopyResult={showCopyResult}
          handleFavoriteSubmit={handleFavoriteSubmit}
          reloadRecord={reloadRecord}
          reload={reload}
          resourceId={id}
          orgChartData={orgChartData}
          styles0={styles0}
          onMenuClick={isOwner ? handleMenuClick : null}
        />
      )}
      <div className={classNames(styles0.copyResult, { hidden: copyResultHidden })}>
        {t('record.result.copyText')}
      </div>
    </CardView>
  )
}

export default DetailContent
